import { SEARCH_UPSELL_TYPENAME } from 'bundles/search-common/constants';
import type { TakeoverMatch } from 'bundles/search-common/experiments/takeoverPilot/getTakeoverMatchForQuery';
import type { SearchHit, SearchUpsellHit } from 'bundles/search-common/providers/searchTypes';

// TAKEOVER_PILOT: hit-array helpers used only by useTakeoverPilotHitPlacement.

// Same device-based slot as the Upsell Card ("spot 8" desktop / "spot 4" mobile).
export const getTakeoverCardPosition = (isMobile: boolean): number => (isMobile ? 3 : 7);

// Moves the hit matching `predicate` to `targetIndex`; other hits keep their relative order.
export const repositionHitToIndex = (
  hits: Array<SearchHit | SearchUpsellHit>,
  targetIndex: number,
  predicate: (hit: SearchHit | SearchUpsellHit) => boolean
): Array<SearchHit | SearchUpsellHit> => {
  if (targetIndex < 0) {
    return hits;
  }

  const currentIndex = hits.findIndex(predicate);
  if (currentIndex === -1 || currentIndex === targetIndex) {
    return hits;
  }

  const reordered = [...hits];
  const [matchedHit] = reordered.splice(currentIndex, 1);
  if (!matchedHit) {
    return hits;
  }
  reordered.splice(Math.min(targetIndex, reordered.length), 0, matchedHit);
  return reordered;
};

// A synthetic hit for an 'insert'-mode campaign, reusing the Upsell Card's synthetic-hit slot.
export const getTakeoverUpsellHit = (takeoverMatch: TakeoverMatch): SearchUpsellHit => ({
  id: `takeover_${takeoverMatch.campaignId}`,
  title: takeoverMatch.mode === 'insert' ? takeoverMatch.headline : takeoverMatch.partnerName,
  breakpoint: 'xs',
  __typename: SEARCH_UPSELL_TYPENAME,
  takeoverCampaignId: takeoverMatch.campaignId,
});

// Inserts `hitToInsert` at `targetIndex`, clamping to the end of the array if out of range.
export const insertHitAtIndex = (
  hits: Array<SearchHit | SearchUpsellHit>,
  targetIndex: number,
  hitToInsert: SearchUpsellHit
): Array<SearchHit | SearchUpsellHit> => {
  if (targetIndex < 0) {
    return hits;
  }

  const clampedIndex = Math.min(targetIndex, hits.length);
  return [...hits.slice(0, clampedIndex), hitToInsert, ...hits.slice(clampedIndex)];
};
